export const BUSINESS_TIMEZONE = 'Australia/Melbourne'
export const SLOT_UNAVAILABLE_MESSAGE =
  'That appointment has just become unavailable. Please choose another time.'

export const APPOINTMENT_STATUSES = ['not_selected', 'reserved', 'confirmed', 'cancelled'] as const
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number]

export type BookingPaymentStatus =
  | 'unpaid'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'refunded'
  | 'partially_refunded'

export type BookStepId = 'protection' | 'addons' | 'appointment' | 'deposit' | 'confirmation'

export const DEFAULT_SLOT_HOLD_MINUTES = 20

export interface AvailabilitySettings {
  timezone: string
  minNoticeMinutes: number
  maxDaysAhead: number
  slotDurationMinutes: number
  slotIntervalMinutes: number
  slotCapacity: number
  slotHoldMinutes: number
}

export interface AvailabilityRule {
  dayOfWeek: number
  startTime: string
  endTime: string
  isClosed: boolean
}

export interface TimeRange {
  startAt: string
  endAt: string
}

export interface OccupiedSlot extends TimeRange {
  bookingId: string
  status: AppointmentStatus
  holdExpiresAt?: string | null
}

export interface GeneratedSlot {
  date: string
  time: string
  startAt: string
  endAt: string
  available: boolean
}

export interface AvailableDate {
  date: string
  label: string
  availableSlotCount: number
}

export type ReservationFailureReason =
  | 'unpaid'
  | 'past'
  | 'outside_hours'
  | 'blocked'
  | 'full'
  | 'outside_horizon'
  | 'invalid_slot'

export type ReservationDecision =
  | {
      ok: true
      appointmentStatus: 'reserved' | 'confirmed'
      startAt: string
      endAt: string
      timezone: string
      holdExpiresAt?: string
    }
  | {
      ok: false
      reason: ReservationFailureReason
      message: string
    }

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

export function pad2(value: number) {
  return String(value).padStart(2, '0')
}

export function parseYmd(date: string) {
  const [year, month, day] = date.split('-').map((part) => Number(part))
  return { year, month, day }
}

export function parseHm(time: string) {
  const [hour, minute] = time.split(':').map((part) => Number(part))
  return { hour: hour ?? 0, minute: minute ?? 0 }
}

function timeZoneOffsetMinutes(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)

  const map: Record<string, string> = {}
  for (const part of parts) {
    if (part.type !== 'literal') map[part.type] = part.value
  }

  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour === '24' ? '0' : map.hour),
    Number(map.minute),
    Number(map.second),
  )
  return (asUtc - instant.getTime()) / 60_000
}

export function makeZonedDateTime(date: string, time: string, timeZone: string) {
  const { year, month, day } = parseYmd(date)
  const { hour, minute } = parseHm(time)
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0))
  const offset1 = timeZoneOffsetMinutes(utcGuess, timeZone)
  let corrected = new Date(utcGuess.getTime() - offset1 * 60_000)
  const offset2 = timeZoneOffsetMinutes(corrected, timeZone)
  if (offset2 !== offset1) {
    corrected = new Date(utcGuess.getTime() - offset2 * 60_000)
  }
  return corrected
}

export function ymdInTimeZone(instant: Date, timeZone: string) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant)
}

export function hmInTimeZone(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant)
  const hour = parts.find((part) => part.type === 'hour')?.value ?? '00'
  const minute = parts.find((part) => part.type === 'minute')?.value ?? '00'
  return `${hour}:${minute}`
}

export function addDaysToYmd(date: string, days: number) {
  const { year, month, day } = parseYmd(date)
  const utc = new Date(Date.UTC(year, month - 1, day + days))
  return `${utc.getUTCFullYear()}-${pad2(utc.getUTCMonth() + 1)}-${pad2(utc.getUTCDate())}`
}

export function weekdayInTimeZone(date: string, timeZone: string) {
  const { year, month, day } = parseYmd(date)
  const utcNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
  }).format(utcNoon)
  return WEEKDAY_INDEX[weekday] ?? 0
}

export function formatAppointmentDate(startAt: string, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).formatToParts(new Date(startAt))
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''
  return `${value('weekday')}, ${value('day')} ${value('month')} ${value('year')}`
}

export function formatAppointmentTime(startAt: string, timeZone: string) {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(startAt)).toLowerCase()
}

export function formatSlotTimeLabel(time: string) {
  const { hour, minute } = parseHm(time)
  const suffix = hour >= 12 ? 'pm' : 'am'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${hour12}:${pad2(minute)} ${suffix}`
}

export function canViewAvailability(_paymentStatus?: string | null) {
  return true
}

export function isActiveAppointmentStatus(status: AppointmentStatus) {
  return status === 'reserved' || status === 'confirmed'
}

export function isOccupyingAppointment(item: OccupiedSlot, now: Date) {
  if (item.status === 'confirmed') return true
  if (item.status !== 'reserved') return false
  if (!item.holdExpiresAt) return true
  return Date.parse(item.holdExpiresAt) > now.getTime()
}

export function isUnexpiredReservation(input: {
  appointmentStatus?: string | null
  holdExpiresAt?: string | null
  paymentStatus?: string | null
  now?: Date
}) {
  if (input.appointmentStatus === 'confirmed') return true
  if (input.appointmentStatus !== 'reserved') return false
  if (input.paymentStatus === 'paid') return true
  if (!input.holdExpiresAt) return false
  return Date.parse(input.holdExpiresAt) > (input.now ?? new Date()).getTime()
}

export function reservationHoldExpiresAt(now: Date, holdMinutes: number) {
  return new Date(now.getTime() + holdMinutes * 60_000).toISOString()
}

function rangesOverlap(left: TimeRange, right: TimeRange) {
  return Date.parse(left.startAt) < Date.parse(right.endAt)
    && Date.parse(right.startAt) < Date.parse(left.endAt)
}

function ruleForDate(date: string, rules: AvailabilityRule[], timeZone: string) {
  const dayOfWeek = weekdayInTimeZone(date, timeZone)
  return rules.find((rule) => rule.dayOfWeek === dayOfWeek) ?? null
}

function minutesFromMidnight(time: string) {
  const { hour, minute } = parseHm(time)
  return hour * 60 + minute
}

export function generateSlotsForDate(input: {
  date: string
  now: Date
  settings: AvailabilitySettings
  rules: AvailabilityRule[]
  exceptions: TimeRange[]
  occupied: OccupiedSlot[]
  ignoreBookingId?: string
}): GeneratedSlot[] {
  const { date, now, settings, rules, exceptions, occupied, ignoreBookingId } = input
  const rule = ruleForDate(date, rules, settings.timezone)
  if (!rule || rule.isClosed) return []

  const startMinutes = minutesFromMidnight(rule.startTime)
  const endMinutes = minutesFromMidnight(rule.endTime)
  const duration = settings.slotDurationMinutes
  const interval = settings.slotIntervalMinutes
  const minNoticeAt = new Date(now.getTime() + settings.minNoticeMinutes * 60_000)
  const today = ymdInTimeZone(now, settings.timezone)
  const lastDate = addDaysToYmd(today, settings.maxDaysAhead)
  const slots: GeneratedSlot[] = []

  for (let minutes = startMinutes; minutes + duration <= endMinutes; minutes += interval) {
    const time = `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`
    const start = makeZonedDateTime(date, time, settings.timezone)
    const end = new Date(start.getTime() + duration * 60_000)
    const range = { startAt: start.toISOString(), endAt: end.toISOString() }
    const occupiedCount = occupied.filter((item) => (
      item.bookingId !== ignoreBookingId
      && isOccupyingAppointment(item, now)
      && rangesOverlap(item, range)
    )).length

    const available = date >= today
      && date <= lastDate
      && start >= minNoticeAt
      && !exceptions.some((item) => rangesOverlap(item, range))
      && occupiedCount < settings.slotCapacity

    slots.push({
      date,
      time,
      startAt: range.startAt,
      endAt: range.endAt,
      available,
    })
  }

  return slots
}

export function listAvailableDates(input: {
  now: Date
  settings: AvailabilitySettings
  rules: AvailabilityRule[]
  exceptions: TimeRange[]
  occupied: OccupiedSlot[]
  ignoreBookingId?: string
}): AvailableDate[] {
  const today = ymdInTimeZone(input.now, input.settings.timezone)
  const dates: AvailableDate[] = []

  for (let offset = 0; offset <= input.settings.maxDaysAhead; offset += 1) {
    const date = addDaysToYmd(today, offset)
    const slots = generateSlotsForDate({ ...input, date })
    const availableSlotCount = slots.filter((slot) => slot.available).length
    if (availableSlotCount === 0) continue
    dates.push({
      date,
      label: formatAppointmentDate(makeZonedDateTime(date, '12:00', input.settings.timezone).toISOString(), input.settings.timezone),
      availableSlotCount,
    })
  }

  return dates
}

export function evaluateReservation(input: {
  booking: { id: string; paymentStatus: string; appointmentStatus?: AppointmentStatus }
  slotStartAt: string
  now: Date
  settings: AvailabilitySettings
  rules: AvailabilityRule[]
  exceptions: TimeRange[]
  occupied: OccupiedSlot[]
}): ReservationDecision {
  const start = new Date(input.slotStartAt)
  if (Number.isNaN(start.getTime())) {
    return { ok: false, reason: 'invalid_slot', message: 'Choose a valid appointment time.' }
  }

  const date = ymdInTimeZone(start, input.settings.timezone)
  const today = ymdInTimeZone(input.now, input.settings.timezone)
  const lastDate = addDaysToYmd(today, input.settings.maxDaysAhead)
  if (date < today || start.getTime() < input.now.getTime()) {
    return { ok: false, reason: 'past', message: 'Choose a future appointment time.' }
  }
  if (date > lastDate) {
    return { ok: false, reason: 'outside_horizon', message: 'That appointment is outside the booking window.' }
  }

  const slots = generateSlotsForDate({
    date,
    now: input.now,
    settings: input.settings,
    rules: input.rules,
    exceptions: input.exceptions,
    occupied: input.occupied,
    ignoreBookingId: input.booking.id,
  })
  const match = slots.find((slot) => slot.startAt === start.toISOString())
  if (!match) {
    const rule = ruleForDate(date, input.rules, input.settings.timezone)
    if (!rule || rule.isClosed) {
      return { ok: false, reason: 'outside_hours', message: 'Autoglow is closed at that time.' }
    }
    return { ok: false, reason: 'invalid_slot', message: 'Choose an available appointment time.' }
  }

  const minNoticeAt = new Date(input.now.getTime() + input.settings.minNoticeMinutes * 60_000)
  if (start < minNoticeAt) {
    return { ok: false, reason: 'past', message: 'Choose a future appointment time.' }
  }

  const range = { startAt: match.startAt, endAt: match.endAt }
  if (input.exceptions.some((item) => rangesOverlap(item, range))) {
    return { ok: false, reason: 'blocked', message: SLOT_UNAVAILABLE_MESSAGE }
  }

  const occupiedCount = input.occupied.filter((item) => (
    item.bookingId !== input.booking.id
    && isOccupyingAppointment(item, input.now)
    && rangesOverlap(item, range)
  )).length
  if (occupiedCount >= input.settings.slotCapacity) {
    return { ok: false, reason: 'full', message: SLOT_UNAVAILABLE_MESSAGE }
  }

  const paid = input.booking.paymentStatus === 'paid'
  return {
    ok: true,
    appointmentStatus: paid ? 'confirmed' : 'reserved',
    startAt: match.startAt,
    endAt: match.endAt,
    timezone: input.settings.timezone,
    holdExpiresAt: paid
      ? undefined
      : reservationHoldExpiresAt(input.now, input.settings.slotHoldMinutes),
  }
}

export function claimSlot(
  occupancy: Map<string, string[]>,
  slotStartAt: string,
  bookingId: string,
  capacity: number,
): ReservationDecision {
  const holders = occupancy.get(slotStartAt) ?? []
  if (holders.includes(bookingId)) {
    return {
      ok: true,
      appointmentStatus: 'reserved',
      startAt: slotStartAt,
      endAt: slotStartAt,
      timezone: BUSINESS_TIMEZONE,
    }
  }
  if (holders.length >= capacity) {
    return { ok: false, reason: 'full', message: SLOT_UNAVAILABLE_MESSAGE }
  }
  occupancy.set(slotStartAt, [...holders, bookingId])
  return {
    ok: true,
    appointmentStatus: 'reserved',
    startAt: slotStartAt,
    endAt: slotStartAt,
    timezone: BUSINESS_TIMEZONE,
  }
}

function releaseSlot(occupancy: Map<string, string[]>, slotStartAt: string | null | undefined, bookingId: string) {
  if (!slotStartAt) return
  const remaining = (occupancy.get(slotStartAt) ?? []).filter((id) => id !== bookingId)
  if (remaining.length === 0) occupancy.delete(slotStartAt)
  else occupancy.set(slotStartAt, remaining)
}

export function applySlotChange(input: {
  occupancy: Map<string, string[]>
  bookingId: string
  previousStartAt?: string | null
  nextStartAt: string
  capacity: number
}): ReservationDecision {
  releaseSlot(input.occupancy, input.previousStartAt, input.bookingId)
  const claimed = claimSlot(input.occupancy, input.nextStartAt, input.bookingId, input.capacity)
  if (!claimed.ok && input.previousStartAt) {
    claimSlot(input.occupancy, input.previousStartAt, input.bookingId, input.capacity)
  }
  return claimed
}

export function resolveVisibleBookStep(input: {
  requestedStep: BookStepId
  paymentStatus?: string | null
  appointmentStatus?: AppointmentStatus | null
  holdExpiresAt?: string | null
  now?: Date
}): BookStepId {
  const paid = input.paymentStatus === 'paid'
  const confirmed = input.appointmentStatus === 'confirmed'
  const reserved = isUnexpiredReservation({
    appointmentStatus: input.appointmentStatus,
    holdExpiresAt: input.holdExpiresAt,
    paymentStatus: input.paymentStatus,
    now: input.now,
  })

  if (input.requestedStep === 'confirmation' && !(paid && confirmed)) {
    if (paid) return 'appointment'
    return reserved ? 'deposit' : 'appointment'
  }
  if (input.requestedStep === 'deposit' && !paid && !reserved) {
    return 'appointment'
  }
  return input.requestedStep
}

export function buildConfirmationSummary(input: {
  publicReference: string
  packageDisplayName: string
  productName: string
  addOnNames: string[]
  noAdditionalProtection?: boolean
  depositAmountCents: number
  appointmentStartAt: string
  appointmentTimezone: string
  location: string
}) {
  const depositDollars = Math.round(input.depositAmountCents / 100)
  return {
    publicReference: input.publicReference,
    protection: input.packageDisplayName,
    productName: input.productName,
    addOnsLabel: input.noAdditionalProtection || input.addOnNames.length === 0
      ? 'No Additional Protection'
      : input.addOnNames.join(', '),
    depositPaidLabel: `A$${depositDollars} Deposit Paid`,
    appointmentDate: formatAppointmentDate(input.appointmentStartAt, input.appointmentTimezone),
    appointmentTime: formatAppointmentTime(input.appointmentStartAt, input.appointmentTimezone),
    location: input.location,
    remainingBalanceNote:
      'Your $100 deposit has been applied to your booking. The remaining balance will be invoiced separately.',
  }
}

export function defaultAvailabilitySettings(
  row?: Partial<{
    timezone: string
    min_notice_minutes: number
    max_days_ahead: number
    slot_interval_minutes: number
    slot_duration_minutes: number
    slot_capacity: number
    slot_hold_minutes: number
  }> | null,
): AvailabilitySettings {
  const duration = row?.slot_duration_minutes ?? row?.slot_interval_minutes ?? 60
  const hold = row?.slot_hold_minutes ?? DEFAULT_SLOT_HOLD_MINUTES
  return {
    timezone: row?.timezone || BUSINESS_TIMEZONE,
    minNoticeMinutes: row?.min_notice_minutes ?? 120,
    maxDaysAhead: row?.max_days_ahead ?? 30,
    slotDurationMinutes: duration,
    slotIntervalMinutes: row?.slot_interval_minutes ?? duration,
    slotCapacity: row?.slot_capacity ?? 1,
    slotHoldMinutes: hold > 0 && hold <= 120 ? hold : DEFAULT_SLOT_HOLD_MINUTES,
  }
}

export function mapAvailabilityRules(
  rows: Array<{ day_of_week: number; start_time: string; end_time: string; is_closed: boolean }>,
): AvailabilityRule[] {
  return rows.map((row) => ({
    dayOfWeek: row.day_of_week,
    startTime: String(row.start_time).slice(0, 5),
    endTime: String(row.end_time).slice(0, 5),
    isClosed: row.is_closed,
  }))
}
